import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Plus, Trash2, CheckCheck, X, Search, RefreshCw, ShieldAlert } from "lucide-react";

function AlertsTable({ alerts, onAck, onDismiss, loading }) {
  if (loading) return <div className="text-sm text-muted-foreground font-mono py-8 text-center">yükleniyor...</div>;
  if (!alerts.length) return <div className="text-sm text-muted-foreground font-mono py-8 text-center">uyarı yok 🎉</div>;
  return (
    <table className="data-table" data-testid="alerts-table">
      <thead>
        <tr>
          <th>Domain</th>
          <th>Kelime</th>
          <th>Alan</th>
          <th>Tip</th>
          <th>Eşleşen</th>
          <th>Durum</th>
          <th>Zaman</th>
          <th className="text-right">İşlem</th>
        </tr>
      </thead>
      <tbody>
        {alerts.map((a) => (
          <tr key={a.id} data-testid={`alert-${a.id}`}>
            <td className="font-mono text-xs font-semibold">{a.domain}</td>
            <td><span className="status-badge err">{a.keyword}</span></td>
            <td className="font-mono text-[10px] text-muted-foreground">{a.matched_field}</td>
            <td className="font-mono text-[10px]">{a.record_type || "—"}</td>
            <td className="font-mono text-xs max-w-xs truncate" title={a.matched_text}>{a.matched_text}</td>
            <td>
              {a.status === "new" && <span className="status-badge warn">yeni</span>}
              {a.status === "acked" && <span className="status-badge ok">onaylandı</span>}
              {a.status === "dismissed" && <span className="status-badge muted">kapatıldı</span>}
            </td>
            <td className="font-mono text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString("tr-TR")}</td>
            <td>
              <div className="flex justify-end gap-1">
                {a.status === "new" && (
                  <Button size="sm" variant="ghost" onClick={() => onAck(a.id)} title="İncelendi">
                    <CheckCheck size={13} className="text-emerald-400" />
                  </Button>
                )}
                {a.status !== "dismissed" && (
                  <Button size="sm" variant="ghost" onClick={() => onDismiss(a.id)} title="Yanlış alarm / kapat">
                    <X size={13} className="text-muted-foreground" />
                  </Button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ContentAlerts() {
  const [keywords, setKeywords] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [filter, setFilter] = useState("new");
  const [q, setQ] = useState("");

  const [newWord, setNewWord] = useState("");
  const [adding, setAdding] = useState(false);
  const [rescanning, setRescanning] = useState(false);

  const loadKeywords = async () => {
    try {
      const { data } = await api.get("/content-keywords");
      setKeywords(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadAlerts = async () => {
    setAlertsLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (q) params.q = q;
      const { data } = await api.get("/content-alerts", { params });
      setAlerts(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(data?.total || 0);
      setNewCount(data?.new_count || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAlertsLoading(false);
    }
  };

  useEffect(() => { loadKeywords(); }, []);
  useEffect(() => { loadAlerts(); const iv = setInterval(loadAlerts, 20000); return () => clearInterval(iv); }, [filter, q]);

  const addKeyword = async () => {
    const w = newWord.trim();
    if (w.length < 2) { toast.error("En az 2 karakter"); return; }
    setAdding(true);
    try {
      await api.post("/content-keywords", { word: w, enabled: true });
      toast.success("Kelime eklendi");
      setNewWord("");
      loadKeywords();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAdding(false); }
  };

  const toggleKeyword = async (k) => {
    try {
      await api.patch(`/content-keywords/${k.id}`, null, { params: { enabled: !k.enabled } });
      loadKeywords();
    } catch (e) {
      // Fallback: some backends expect JSON body
      try {
        await api.patch(`/content-keywords/${k.id}`, { enabled: !k.enabled });
        loadKeywords();
      } catch (e2) { toast.error(formatApiError(e2)); }
    }
  };

  const deleteKeyword = async (k) => {
    if (!window.confirm(`"${k.word}" kelimesini sil?`)) return;
    try {
      await api.delete(`/content-keywords/${k.id}`);
      loadKeywords();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const ack = async (id) => {
    try { await api.post(`/content-alerts/${id}/ack`); loadAlerts(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const dismiss = async (id) => {
    try { await api.post(`/content-alerts/${id}/dismiss`); loadAlerts(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const rescan = async () => {
    if (!window.confirm("Tüm zone'lar yeniden taransın mı? (Aynı eşleşme son 24 saat içinde varsa duplicate oluşmaz)")) return;
    setRescanning(true);
    try {
      const { data } = await api.post("/content-alerts/rescan");
      toast.success(`${data.zones_scanned} zone tarandı, ${data.new_alerts} yeni uyarı`);
      loadAlerts();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRescanning(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <ShieldAlert size={22} /> İçerik Uyarıları
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Zone kayıtlarında yasaklı kelime taraması · {keywords.filter(k => k.enabled).length} aktif kelime · <span className="text-red-400 font-semibold">{newCount} yeni uyarı</span>
          </p>
        </div>
        <Button onClick={rescan} disabled={rescanning} variant="secondary" data-testid="rescan-btn">
          <RefreshCw size={14} className={`mr-1.5 ${rescanning ? "animate-spin" : ""}`} />
          {rescanning ? "Taranıyor..." : "Tüm Zone'ları Tara"}
        </Button>
      </div>

      {/* Keywords CRUD */}
      <div className="border border-border bg-card rounded-md p-5">
        <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <AlertTriangle size={14} /> Yasaklı Kelimeler
        </h2>
        <div className="flex gap-2 mb-3">
          <Input
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addKeyword()}
            placeholder="Örn: bahis, casino, bet..."
            className="font-mono max-w-md"
            data-testid="keyword-input"
          />
          <Button onClick={addKeyword} disabled={adding} data-testid="keyword-add">
            <Plus size={13} className="mr-1.5" /> Ekle
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {keywords.length === 0 && <span className="text-sm text-muted-foreground font-mono">henüz kelime yok</span>}
          {keywords.map((k) => (
            <div key={k.id} data-testid={`keyword-${k.id}`} className={`inline-flex items-center gap-2 px-2 py-1 rounded-md border ${k.enabled ? "border-border bg-accent/40" : "border-border bg-background opacity-50"}`}>
              <Switch checked={k.enabled} onCheckedChange={() => toggleKeyword(k)} className="scale-75" />
              <span className="font-mono text-xs">{k.word}</span>
              <button onClick={() => deleteKeyword(k)} className="text-muted-foreground hover:text-red-400" title="Sil">
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-muted-foreground font-mono mt-3">
          Tarama alanları: domain adı, kayıt adları (ör. <span className="text-foreground">bet.ornek.com</span>), A/AAAA/CNAME/MX hedefleri, TXT içerikleri. Türkçe karakterler normalize edilir (İ→i, Ş→s, ...). Word-boundary eşleşmesi — <span className="text-foreground">cabinet</span> kelimesi <span className="text-foreground">bet</span>'i tetiklemez.
        </div>
      </div>

      {/* Alerts inbox */}
      <div className="border border-border bg-card rounded-md">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-sm font-semibold">Tespit Edilen Uyarılar</h2>
          <div className="flex gap-2">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="domain/kelime..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-7 h-8 text-xs w-56" />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">Yeni</SelectItem>
                <SelectItem value="acked">Onaylandı</SelectItem>
                <SelectItem value="dismissed">Kapatıldı</SelectItem>
                <SelectItem value="all">Tümü</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <AlertsTable alerts={alerts} onAck={ack} onDismiss={dismiss} loading={alertsLoading} />
        </div>
      </div>
    </div>
  );
}
